import { type ReactNode, useRef } from 'react';

import { FocusRegistry } from './focus-registry';
import { FocusManagerContext } from './FocusManagerContext';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface FocusManagerProviderProps {
  children: ReactNode;
}

/**
 * Provides a FocusRegistry to the React subtree.
 *
 * Mount ONCE inside ERPCoreProvider (and inside ShortcutManagerProvider so
 * that focus-related shortcuts have access to both managers).
 *
 * The registry is created once and never replaced — it is the authoritative
 * source of truth for all focus scope state in the application.
 *
 * @example
 * <FocusManagerProvider>
 *   <App />
 * </FocusManagerProvider>
 */
export function FocusManagerProvider({ children }: FocusManagerProviderProps) {
  // Create registry once (stable across re-renders)
  const registryRef = useRef<FocusRegistry | null>(null);
  registryRef.current ??= new FocusRegistry();

  return (
    <FocusManagerContext.Provider value={{ registry: registryRef.current }}>
      {children}
    </FocusManagerContext.Provider>
  );
}
